import React, { useState, useEffect, useCallback } from 'react';
import { FaStar, FaCheck, FaTimes, FaVideo, FaFilter, FaClock, FaUser, FaExclamationTriangle, FaSync } from 'react-icons/fa';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const SubmissionsDashboard = () => {
  const [submissions, setSubmissions] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [lessonFilter, setLessonFilter] = useState('');
  const [gradingSubmission, setGradingSubmission] = useState(null);
  const [gradeData, setGradeData] = useState({ grade: 8, status: 'approved', feedback: '' });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  };

  const fetchLessons = async () => {
    try {
      const res = await fetch(`${API_BASE}/lessons`, { headers: getHeaders() });
      const data = await res.json();
      if (data.success) setLessons(data.data);
    } catch (e) { }
  };

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (lessonFilter) params.append('lesson', lessonFilter);
      const res = await fetch(`${API_BASE}/submissions?${params}`, { headers: getHeaders() });
      const data = await res.json();
      if (data.success) {
        setSubmissions(data.data.submissions);
      } else {
        setFetchError(data.message || 'Failed to load submissions');
        setSubmissions([]);
      }
    } catch (e) {
      console.error(e);
      setFetchError('Network error: could not reach server');
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, lessonFilter]);

  useEffect(() => { fetchLessons(); }, []);
  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const openGrade = (submission) => {
    setGradingSubmission(submission);
    setGradeData({ grade: 8, status: 'approved', feedback: '' });
  };

  const handleGrade = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/submissions/${gradingSubmission._id}/grade`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(gradeData)
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: `Submission ${gradeData.status}!` });
        setGradingSubmission(null);
        fetchSubmissions();
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Server error' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const statusBadge = (status) => {
    const map = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${map[status]}`}>{status}</span>;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <FaStar className="text-orange-500 text-2xl" />
        <h2 className="text-xl font-bold text-gray-900">Student Submissions & Grading</h2>
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {message.text}
        </div>
      )}

      {fetchError && (
        <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-200 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <FaExclamationTriangle className="flex-shrink-0" />
            <span><strong>Error loading submissions:</strong> {fetchError}</span>
          </div>
          <button
            onClick={fetchSubmissions}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-xs font-medium transition"
          >
            <FaSync className="text-xs" /> Retry
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6 p-4 bg-gray-50 rounded-xl">
        <div className="flex items-center gap-2">
          <FaFilter className="text-gray-400 text-sm" />
          <span className="text-sm font-medium text-gray-700">Filters:</span>
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <select
          value={lessonFilter}
          onChange={e => setLessonFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
        >
          <option value="">All Lessons</option>
          {lessons.map(l => <option key={l._id} value={l._id}>{l.title}</option>)}
        </select>
      </div>

      {/* Grade Modal */}
      {gradingSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Grade Submission</h3>
            <p className="text-sm text-gray-500 mb-4">
              Student: <span className="font-medium text-gray-800">{gradingSubmission.student?.name}</span> &bull; 
              Video: <span className="font-medium text-gray-800">{gradingSubmission.video?.title}</span>
            </p>

            <div className="mb-5 rounded-xl overflow-hidden bg-black aspect-video">
              <video
                src={gradingSubmission.fileUrl}
                controls
                className="w-full h-full object-contain"
              />
            </div>

            <form onSubmit={handleGrade} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Approval Decision *
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setGradeData(p => ({ ...p, status: 'approved' }))}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 font-medium text-sm transition ${gradeData.status === 'approved' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:border-green-300'}`}
                  >
                    <FaCheck /> Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => setGradeData(p => ({ ...p, status: 'rejected' }))}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 font-medium text-sm transition ${gradeData.status === 'rejected' ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 text-gray-600 hover:border-red-300'}`}
                  >
                    <FaTimes /> Reject
                  </button>
                </div>
              </div>

              {gradeData.status === 'approved' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Grade: <span className="text-orange-600 font-bold text-lg">{gradeData.grade}</span> / 10
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    step="1"
                    value={gradeData.grade}
                    onChange={e => setGradeData(p => ({ ...p, grade: parseInt(e.target.value) }))}
                    className="w-full accent-orange-500"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    {[0,1,2,3,4,5,6,7,8,9,10].map(n => <span key={n}>{n}</span>)}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Feedback (optional)</label>
                <textarea
                  rows={2}
                  value={gradeData.feedback}
                  onChange={e => setGradeData(p => ({ ...p, feedback: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                  placeholder="Leave a note for the student..."
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-2.5 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Submit Grade'}
                </button>
                <button
                  type="button"
                  onClick={() => setGradingSubmission(null)}
                  className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-gray-500">Loading submissions...</div>
      ) : submissions.length === 0 ? (
        <div className="text-center py-10 text-gray-400">
          <FaVideo className="text-5xl mx-auto mb-3 opacity-30" />
          <p>No submissions found for the selected filters.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {submissions.map(sub => (
            <div key={sub._id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-orange-300 transition">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 flex-shrink-0">
                  <FaUser className="text-sm" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{sub.student?.name}</p>
                  <p className="text-sm text-gray-500">{sub.student?.email}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Lesson: <span className="text-gray-600">{sub.lesson?.title || 'N/A'}</span> &bull;&nbsp;
                    Video: <span className="text-gray-600">{sub.video?.title || 'N/A'}</span>
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    {statusBadge(sub.status)}
                    {sub.grade !== null && sub.grade !== undefined && (
                      <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                        {sub.grade}/10
                      </span>
                    )}
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <FaClock className="text-xs" />
                      {new Date(sub.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {sub.status === 'pending' && (
                  <button
                    onClick={() => openGrade(sub)}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition"
                  >
                    <FaStar className="text-xs" /> Grade
                  </button>
                )}
                {sub.status !== 'pending' && (
                  <button
                    onClick={() => openGrade(sub)}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
                  >
                    Re-grade
                  </button>
                )}
                <a
                  href={sub.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                  title="Watch submission"
                >
                  <FaVideo />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SubmissionsDashboard;
